import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, FormControlLabel, Checkbox } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSelector } from 'react-redux';
import { RootState } from '../../reducers';
import { PlanStatusEnum } from '../../Enum/PlanStatusEnum';
import SafetyPlan from '../IntegratedCarePlan/SafetyPlan/SafetyPlan';
import { Stack } from '@mui/system';
import { setHasReviewed } from '../../store/behaviorOrSafetyPlanSlice.slice';
import { useAppDispatch } from '../../store/store';

interface SafetyPlanPromptModalProps {
    open: boolean;
    onCancel: () => void;
    onComplete: () => void;
}

const SafetyPlanPromptModal: React.FC<SafetyPlanPromptModalProps> = ({ open, onCancel, onComplete }) => {
    const { hasReviewed, safetyPlanInfo } = useSelector((state: RootState) => state.safetyPlanSlice);
    const dispatch = useAppDispatch();
    const isCreated = safetyPlanInfo?.Id !== undefined && safetyPlanInfo?.Status !== PlanStatusEnum.NotStarted;
    const canContinue = safetyPlanInfo?.Status !== undefined && isCreated;
    const subtitle = isCreated ? 'review' : 'create or decline';
    const getSafetyPlanMessage = (status: PlanStatusEnum | undefined, canContinue: boolean): string => {
        if (!canContinue) {
            return 'You must create or decline the Safety Plan before continuing.';
        }
        if (status === PlanStatusEnum.Declined) {
            return 'Safety Plan declined.';
        }
        if (status === PlanStatusEnum.Created && hasReviewed) {
            return 'Safety Plan created and reviewed.';
        }
        return '';
    };

    return (
        <Dialog open={open} fullWidth maxWidth="xl" disableEscapeKeyDown PaperProps={{ sx: { height: '95vh' } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <WarningAmberIcon color="error" />
                    <Typography variant="h6" fontWeight="bold" color="error">
                        Safety Plan Required
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    This assessment contains answers that indicate a Safety Plan may be needed. Please {subtitle} the Safety Plan before continuing.
                </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0, overflowY: 'auto' }}>
                <SafetyPlan />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                <Typography variant="body2" color={canContinue ? 'success.main' : 'text.secondary'}>
                    {getSafetyPlanMessage(safetyPlanInfo?.Status, canContinue)}
                </Typography>
                <Stack direction="row" spacing={2}>
                    {safetyPlanInfo?.Status !== PlanStatusEnum.NotStarted && (
                        <FormControlLabel
                            control={<Checkbox checked={hasReviewed} onChange={(e) => dispatch(setHasReviewed(e.target.checked))} />}
                            label="I have reviewed with member."
                        />
                    )}
                    <Button variant="outlined" color="primary" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="primary" disabled={!canContinue || (isCreated && !hasReviewed)} onClick={onComplete}>
                        Continue to Assessment
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default SafetyPlanPromptModal;
